<template>
  <Modal :show="show" class-name="reprocess-modal-container" @close="close">
    <div class="modal-main">
      <div class="modal-header">
        <h2 class="modal-title">Reprocess Sample</h2>
      </div>

      <div class="modal-body">
        <p class="reprocess-note">
          Reprocesses the sample to a <strong>new result version</strong>. Choose, per data source, whether to reuse the
          data already attached to the sample or fetch it again. At least one source must be <strong>Fetch new</strong>.
        </p>

        <b-form-group label="Identify by">
          <b-form-radio-group v-model="identifier" :options="identifierOptions" />
        </b-form-group>

        <b-form-group v-if="identifier === 'resultId'" label="Result ID" label-for="reprocessResultId">
          <b-form-input id="reprocessResultId" v-model="resultId" placeholder="R226080000011" />
          <small v-if="hasVersionSuffix" class="text-muted">
            Version suffix is dropped — reprocess always works off the base result ({{ baseResultId }}).
          </small>
        </b-form-group>

        <b-form-group v-else label="Sample ID" label-for="reprocessSampleId">
          <b-form-input id="reprocessSampleId" v-model="sampleId" placeholder="MXU0018" />
        </b-form-group>

        <div class="sources">
          <div v-for="source in sources" :key="source.key" class="source-row">
            <div class="source-header">
              <span class="source-label">{{ source.label }}</span>
              <b-form-radio-group
                v-model="modes[source.key]"
                :options="sourceModeOptions"
                :name="`mode-${source.key}`"
                buttons
                button-variant="outline-primary"
                size="sm"
              />
            </div>

            <div v-if="modes[source.key] === 'FETCH_NEW'" class="source-overrides">
              <template v-if="source.key === 'bio'">
                <b-form-group label="Bio S3 keys (optional, one per line)" label-for="bioS3Keys">
                  <b-form-textarea
                    id="bioS3Keys"
                    v-model="bioS3KeysText"
                    rows="3"
                    placeholder="Leave blank to auto-discover the latest bio files"
                  />
                </b-form-group>
              </template>

              <template v-if="source.key === 'survey'">
                <b-form-group label="Survey response UUID (optional)" label-for="surveyResponseUuid">
                  <b-form-input
                    id="surveyResponseUuid"
                    v-model="surveyResponseUuid"
                    placeholder="Leave blank to auto-discover the latest survey response"
                  />
                </b-form-group>
              </template>

              <template v-if="source.key === 'lab'">
                <b-form-group label="Lab import ID (optional)" label-for="labImportId">
                  <b-form-input
                    id="labImportId"
                    v-model="labImportId"
                    type="number"
                    placeholder="Leave blank to auto-discover the latest lab import"
                  />
                </b-form-group>
                <b-form-checkbox v-model="labFreshImport">Force a fresh lab import</b-form-checkbox>
              </template>
            </div>
          </div>
        </div>

        <b-form-group label="Content snapshot" label-for="contentSnapshotId">
          <b-form-select
            id="contentSnapshotId"
            v-model="contentSnapshotId"
            :options="snapshotOptions"
            :disabled="snapshotsLoading"
          >
            <template #first>
              <b-form-select-option :value="null">— Not specified —</b-form-select-option>
            </template>
          </b-form-select>
          <small v-if="snapshotsLoading" class="text-muted">Loading snapshots…</small>
          <small v-if="snapshotsError" class="text-danger">{{ snapshotsError }}</small>
        </b-form-group>

        <b-form-group v-if="contentSnapshotId" label="Reason" label-for="snapshotReason">
          <b-form-input id="snapshotReason" v-model="snapshotReason" placeholder="e.g. Update to v1.1" />
        </b-form-group>

        <div v-if="validationError" class="alert alert-warning">
          {{ validationError }}
        </div>

        <div v-if="localError" class="alert alert-danger">
          {{ localError }}
        </div>

        <div v-if="accepted" class="alert alert-success">
          Accepted — tracking ID <strong>{{ accepted.trackingId }}</strong> for sample
          <strong>{{ accepted.resolvedSampleId }}</strong
          >.
          <div v-if="jobStatus">Job status: {{ jobStatus.status }}</div>
          <div v-if="jobStatus && jobStatus.errorMessage" class="text-danger">{{ jobStatus.errorMessage }}</div>
        </div>

        <div class="d-flex justify-content-between">
          <Button
            text="Reprocess"
            :loading="loading"
            :success="buttonSuccess"
            :disabled="Boolean(validationError)"
            @onClick="onClickReprocess"
          />
          <Button text="Close" @onClick="close" />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
  import Modal from '@/components/models/Modal'
  import Button from '@/components/elements/DynamicButton'
  import PodiumReprocessService, { SOURCE_MODES, shortSnapshotId } from '@/services/podiumReprocessService'
  import authService from '@/services/authService'
  import moment from 'moment'

  const POLL_INTERVAL_MS = 5000
  const MAX_POLLS = 30

  export default {
    name: 'ModalReprocess',
    components: { Modal, Button },
    props: {
      show: {
        type: Boolean,
        default: false
      },
      report: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        identifier: 'resultId',
        resultId: '',
        sampleId: '',
        modes: {
          bio: SOURCE_MODES.REUSE,
          survey: SOURCE_MODES.REUSE,
          lab: SOURCE_MODES.REUSE
        },
        bioS3KeysText: '',
        surveyResponseUuid: '',
        labImportId: '',
        labFreshImport: false,
        sources: [
          { key: 'bio', label: 'Bio' },
          { key: 'survey', label: 'Survey' },
          { key: 'lab', label: 'Lab' }
        ],
        identifierOptions: [
          { text: 'Result ID', value: 'resultId' },
          { text: 'Sample ID', value: 'sampleId' }
        ],
        sourceModeOptions: [
          { text: 'Reuse', value: SOURCE_MODES.REUSE },
          { text: 'Fetch new', value: SOURCE_MODES.FETCH_NEW }
        ],
        loading: false,
        buttonSuccess: false,
        localError: null,
        accepted: null,
        jobStatus: null,
        pollTimer: null,
        contentSnapshotId: null,
        snapshotReason: '',
        snapshots: [],
        snapshotsLoading: false,
        snapshotsError: null
      }
    },
    computed: {
      baseResultId() {
        return this.resultId.trim().replace(/-\d+$/, '')
      },
      hasVersionSuffix() {
        return /-\d+$/.test(this.resultId.trim())
      },
      bioS3Keys() {
        return this.bioS3KeysText
          .split('\n')
          .map((key) => key.trim())
          .filter(Boolean)
      },
      snapshotOptions() {
        return [...this.snapshots]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((s) => {
            const date = moment.utc(s.createdAt).local().format('Do MMM YYYY, h:mma')
            const active = s.isActive ? ' (Active)' : ''
            // Wildcard-only content: reprocessing would use the fallback plugin.
            const fallback = s.routingSource === 'FALLBACK' ? ' [FALLBACK]' : ''
            return {
              value: s.snapshotId,
              text: `${s.label ?? shortSnapshotId(s.snapshotId)} — ${date}${active}${fallback}`
            }
          })
      },

      // Mirrors SampleReprocessService.validateRequestShape so operators get feedback
      // before the request leaves the browser.
      validationError() {
        const id = this.identifier === 'resultId' ? this.baseResultId : this.sampleId.trim()
        if (!id) {
          return this.identifier === 'resultId' ? 'A result ID is required.' : 'A sample ID is required.'
        }
        if (!Object.values(this.modes).includes(SOURCE_MODES.FETCH_NEW)) {
          return 'At least one source must be set to Fetch new.'
        }
        if (this.labImportId !== '' && Number.isNaN(Number(this.labImportId))) {
          return 'Lab import ID must be a number.'
        }
        if (this.contentSnapshotId && !this.snapshotReason.trim()) {
          return 'A reason is required when a content snapshot is selected.'
        }
        return null
      }
    },
    watch: {
      show(val) {
        if (val === true) {
          this.resetForm()
          this.loadSnapshots()
        } else {
          this.stopPolling()
        }
      }
    },
    beforeDestroy() {
      this.stopPolling()
    },
    methods: {
      close() {
        this.stopPolling()
        this.$emit('update:show', false)
      },

      resetForm() {
        this.identifier = 'resultId'
        this.resultId = this.report?.reportId || ''
        this.sampleId = this.report?.sampleId || ''
        this.modes = {
          bio: SOURCE_MODES.REUSE,
          survey: SOURCE_MODES.REUSE,
          lab: SOURCE_MODES.REUSE
        }
        this.bioS3KeysText = ''
        this.surveyResponseUuid = ''
        this.labImportId = ''
        this.labFreshImport = false
        this.loading = false
        this.buttonSuccess = false
        this.localError = null
        this.accepted = null
        this.jobStatus = null
        this.contentSnapshotId = null
        this.snapshotReason = ''
      },

      async loadSnapshots() {
        this.snapshotsLoading = true
        this.snapshotsError = null
        try {
          const data = await PodiumReprocessService.getSnapshots()
          this.snapshots = data.snapshots || []
        } catch (error) {
          this.snapshotsError = 'Failed to load snapshots.'
        } finally {
          this.snapshotsLoading = false
        }
      },

      // Overrides are only accepted by the API for sources set to FETCH_NEW, so
      // anything belonging to a REUSE source is left off the payload entirely.
      buildOverrides() {
        const overrides = { labFreshImport: false }

        if (this.modes.bio === SOURCE_MODES.FETCH_NEW && this.bioS3Keys.length) {
          overrides.bioS3Keys = this.bioS3Keys
        }
        if (this.modes.survey === SOURCE_MODES.FETCH_NEW && this.surveyResponseUuid.trim()) {
          overrides.surveyResponseUuid = this.surveyResponseUuid.trim()
        }
        if (this.modes.lab === SOURCE_MODES.FETCH_NEW) {
          if (this.labImportId !== '') {
            overrides.labImportId = Number(this.labImportId)
          }
          overrides.labFreshImport = this.labFreshImport
        }

        return overrides
      },

      async onClickReprocess() {
        if (this.validationError) return

        this.localError = null
        this.accepted = null
        this.jobStatus = null
        this.loading = true

        try {
          const accepted = await PodiumReprocessService.reprocess({
            resultId: this.identifier === 'resultId' ? this.baseResultId : null,
            sampleId: this.identifier === 'sampleId' ? this.sampleId.trim() : null,
            bio: this.modes.bio,
            survey: this.modes.survey,
            lab: this.modes.lab,
            overrides: this.buildOverrides(),
            requestedBy: authService.getUsername(),
            contentSnapshotId: this.contentSnapshotId || undefined,
            reason: this.contentSnapshotId ? this.snapshotReason.trim() : undefined
          })

          this.accepted = accepted
          this.buttonSuccess = true
          this.$emit('accepted', accepted)
          this.startPolling(accepted.trackingId)
        } catch (error) {
          const body = error.response?.data
          this.localError = body?.message || body?.error || error.message
        } finally {
          this.loading = false
        }
      },

      startPolling(trackingId) {
        this.stopPolling()
        let polls = 0

        this.pollTimer = setInterval(async () => {
          polls += 1
          try {
            this.jobStatus = await PodiumReprocessService.getStatus(trackingId)
          } catch (error) {
            this.stopPolling()
            return
          }

          const terminal = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(this.jobStatus.status)
          if (terminal || polls >= MAX_POLLS) {
            this.stopPolling()
          }
        }, POLL_INTERVAL_MS)
      },

      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      }
    }
  }
</script>

<style>
  .reprocess-modal-container {
    max-width: 860px !important;
    width: auto !important;
    height: auto !important;
    padding: 0 !important;
    margin: 20px !important;
    justify-content: flex-start !important;
    background-color: transparent !important;
    box-shadow: none !important;
  }

  .reprocess-modal-container .modal-main {
    background: white;
    border-radius: 8px;
    padding: 24px;
    width: 100%;
    margin: 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .reprocess-modal-container .modal-header {
    margin-bottom: 20px;
    text-align: left;
  }

  .reprocess-modal-container .modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
  }

  .reprocess-modal-container .modal-body {
    padding: 0;
  }

  .reprocess-modal-container .reprocess-note {
    margin-bottom: 20px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.4;
  }

  .reprocess-modal-container .source-row {
    border-top: 1px solid #eee;
    padding: 12px 0;
  }

  .reprocess-modal-container .source-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .reprocess-modal-container .source-label {
    font-weight: 600;
  }

  .reprocess-modal-container .source-overrides {
    margin-top: 12px;
    padding-left: 12px;
    border-left: 3px solid #e4e7f2;
  }

  .reprocess-modal-container .d-flex {
    margin-top: 24px;
    gap: 12px;
  }

  .reprocess-modal-container .btn {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 6px;
  }
</style>
